import { useEffect, useRef, useState } from "react";
import { m as motion, useReducedMotion } from "framer-motion";

interface Post {
  kind: "static" | "video";
  title: string;
  body: string;
  bg: string;
}

const feed: Post[] = [
  { kind: "static", title: "Whitepaper släppt", body: "Nya insikter om B2B-marknadsföring 2026.", bg: "from-slate-700 to-slate-900" },
  { kind: "video", title: "🎬 Bakom kulisserna", body: "Så filmade vi Volvos senaste kampanj.", bg: "from-peach/40 to-mint/20" },
  { kind: "static", title: "Vi flyttar kontor", body: "Snart syns vi på Drottninggatan 12.", bg: "from-stone-700 to-stone-900" },
  { kind: "video", title: "🎬 Maria berättar", body: "11 år som servicetekniker — och varför hon stannar.", bg: "from-mint/30 to-butter/20" },
  { kind: "static", title: "Vi vann pris", body: "Stolt mottagare av Årets Byrå 2025.", bg: "from-zinc-700 to-zinc-900" },
  { kind: "video", title: "🎬 90-sekunders kundresa", body: "Från första mötet till tredje månadens leverans.", bg: "from-peach/30 to-peach/10" },
];

/** Interactive proof of stop-rate: as user dwells, video posts accumulate "time on post". */
export function ThumbStopMoment() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [staticMs, setStaticMs] = useState(0);
  const [videoMs, setVideoMs] = useState(0);
  const startRef = useRef<number | null>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (activeIdx === null) return;
    startRef.current = performance.now();
    const id = setInterval(() => {
      if (startRef.current === null) return;
      const elapsed = performance.now() - startRef.current;
      const post = feed[activeIdx];
      if (post.kind === "video") setVideoMs((m) => m + 100);
      else setStaticMs((m) => m + 100);
      startRef.current = performance.now();
      // Force tick
      void elapsed;
    }, 100);
    return () => clearInterval(id);
  }, [activeIdx]);

  const ratio = staticMs + videoMs > 0
    ? Math.round((videoMs / Math.max(1, staticMs + videoMs)) * 100)
    : 0;

  return (
    <div className="grid gap-6 rounded-3xl border border-white/10 bg-surface/60 p-6 md:grid-cols-[1.2fr_1fr] md:p-10">
      <div>
        <div className="mb-3 text-[11px] uppercase tracking-[0.18em] text-peach">Pröva själv</div>
        <h3 className="text-2xl font-semibold md:text-3xl">Var stannar tummen?</h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          Hovra över ett inlägg som om du skrollade. Vi mäter exakt hur länge du stannar — och vi vågar gissa var sekunderna hamnar.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {feed.map((p, i) => (
            <button
              key={i}
              type="button"
              onMouseEnter={() => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(null)}
              onFocus={() => setActiveIdx(i)}
              onBlur={() => setActiveIdx(null)}
              className={`relative overflow-hidden rounded-xl border bg-gradient-to-br p-4 text-left transition-transform hover:-translate-y-0.5 ${p.bg} ${activeIdx === i ? "border-peach" : "border-white/10"}`}
            >
              {p.kind === "video" && !reduce && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent"
                  animate={{ x: ["-100%", "100%"] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
                />
              )}
              <div className="relative">
                <div className="text-[10px] uppercase tracking-widest opacity-70">
                  {p.kind === "video" ? "Video · 0:24" : "Inlägg"}
                </div>
                <div className="mt-1 text-sm font-semibold text-white">{p.title}</div>
                <div className="mt-1 line-clamp-2 text-xs text-white/70">{p.body}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col justify-center">
        <div className="rounded-2xl border border-white/10 bg-background p-6">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Er dwell-tid</div>
          <div className="mt-4 space-y-4">
            <Row label="På video" ms={videoMs} tone="peach" />
            <Row label="På statiskt" ms={staticMs} tone="ghost" />
          </div>
          <div className="mt-6 border-t border-white/10 pt-4">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Andel tid på rörligt</div>
            <div className="mt-1 font-serif text-4xl text-peach">{ratio}%</div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Plattformarnas algoritmer mäter exakt det här — och belönar rörligt med mer räckvidd.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, ms, tone }: { label: string; ms: number; tone: "peach" | "ghost" }) {
  const secs = (ms / 1000).toFixed(1);
  const max = 10000;
  const pct = Math.min(100, (ms / max) * 100);
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-mono ${tone === "peach" ? "text-peach" : "text-foreground/70"}`}>{secs}s</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/5">
        <motion.div
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.2 }}
          className={`h-full ${tone === "peach" ? "bg-peach" : "bg-white/30"}`}
        />
      </div>
    </div>
  );
}
